import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Align, FixedSizeList as List, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import { isNil, any, append, filter } from 'ramda';
import { IBindingCallback2, IBindingCallback1 } from '../../common/models/callback';
import { Node } from '../../common/models/common/Node';
import LoaderWrapper from '../LoaderWrapper';
import NoDataPlaceholder from '../NoDataPlaceholder';
import styles from './styles.module.scss';
import Icon from '../Icon';

interface ICheckboxListProps {
  items: Node[];
  total: number;
  loading?: boolean;
  defaultChecked?: string[];
  multipleChoice?: boolean;
  onSelect: IBindingCallback1<string[]>;
  loadMore?: IBindingCallback2<number, number>;
}

export interface ICheckboxListHandler {
  changeSelected: IBindingCallback1<string[]>;
  scrollToItem: IBindingCallback2<number, Align>;
}

const CheckboxList = forwardRef<ICheckboxListHandler, ICheckboxListProps>(({
  items,
  total,
  loading = false,
  defaultChecked,
  multipleChoice,
  loadMore = () => null,
  onSelect
}, ref) => {
  const [checked, setChecked] = useState(isNil(defaultChecked) ? [] : defaultChecked);
  const isFirstRun = useRef(true);
  let listRef: List;

  const onRowClick = (index: number) => (
    setChecked(state => {
      const isChecked = any(item => item === items[index].value, state);
      if (multipleChoice) {
        return isChecked ? filter(item => item !== items[index].value, state) : append(items[index].value, state);
      }
      return isChecked ? [] : [items[index].value];
    })
  );

  const loadItems = (from: number, to: number) => {
    loadMore(from, to);
    return null;
  };

  const Row = ({ index, style }: ListChildComponentProps) => {
    const selected = any(item => item === items[index].value, checked);
    const iconUnchecked = multipleChoice ? 'checkboxUnchecked' : 'radioUnchecked';
    const iconChecked = multipleChoice ? 'checkboxChecked' : 'radioChecked';
    return (
      <div
        role="presentation"
        onClick={() => onRowClick(index)}
        style={style}
        className={`${styles.item} rct-text`}
      >
        <Icon className={styles.icon} icon={selected ? iconChecked : iconUnchecked} />
        <div className={styles.label}>{items[index]?.label}</div>
      </div>
    );
  };

  const isRowLoaded = (index: number) => Boolean(items[index]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    onSelect(checked);
  }, [checked]);

  useImperativeHandle(ref, () => ({
    changeSelected(newSelected: string[]) {
      setChecked(newSelected);
    },
    scrollToItem(index: number, align: Align) {
      listRef?.scrollToItem(index, align);
    }
  }));

  return (
    <LoaderWrapper loading={loading || false} transparent>
      {!loading && total === 0
        ? <NoDataPlaceholder />
        : (
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isItemLoaded={isRowLoaded}
                itemCount={total}
                loadMoreItems={loadItems}
                minimumBatchSize={20}
              >
                {({ onItemsRendered, ref: setListRef }) => (
                  <List
                    height={height}
                    itemCount={total}
                    itemSize={30}
                    width={width}
                    onItemsRendered={onItemsRendered}
                    ref={(fixedListRef: List) => {
                      listRef = fixedListRef;
                      (setListRef as IBindingCallback1<List>)(fixedListRef);
                    }}
                  >
                    {Row}
                  </List>
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>
        )}
    </LoaderWrapper>
  );
});

export default CheckboxList;
